
import { defineComponent } from "vue";
import router from "@/router";
import store from "@/store";
import { useRoute } from "vue-router";
import StudentService from "@/api/Aula/StudentsService";
import { Tab } from "bootstrap"; // top of file

import PersonelInformationComponent from "./TabPersonalInformation.vue";
import TabContactComponent from "./TabContact.vue";
import TabParentsComponent from "./TabParents.vue";
import TabAcademicComponent from "./TabAcademic.vue";
import TabHealthComponent from "./TabHealth.vue";
import TabEmergencyComponent from "./TabEmergency.vue";
import TabTransportComponent from "./TabTransport.vue";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";
import { IIndividualEnrollmentModel } from "@/interfaces/modules";

export default defineComponent({
  name: "StudentTab",
  components: {
    PersonelInformationComponent,
    TabContactComponent,
    TabParentsComponent,
    TabAcademicComponent,
    TabHealthComponent,
    TabEmergencyComponent,
    TabTransportComponent
  },
  data() {
    const route = useRoute();
    return {
      wasValidated: false,          // <— drives invalid styles in children
      studentId: route.params.Id || 0,
      file: null as File | null,
      enrollment: {} as IIndividualEnrollmentModel,
      previewUrl: new URL('@/assets/images/user/sin_imagen.png', import.meta.url).href, // default preview
      objectUrl: undefined as string | undefined,
      form: {
        loading: true,
        record: {} as any
      }
    };
  },
  async mounted() {
    if (parseInt(this.studentId as any) > 0) {
      this.form.record = await StudentService.GetStudentsbyId({ Id: this.studentId });
      if (this.form.record.BirthDate) {
        this.form.record.BirthDate = this.form.record.BirthDate.substring(0, 10);
      }
      if (this.form.record.Image)
        this.previewUrl = this.form.record.Image

      this.enrollment = await IndividualEnrollmentService.GetByStudentLastEnrollment(this.form.record.Id as any) as IIndividualEnrollmentModel;
    }
  },
  methods: {
    cancel() { router.push({ name: "Students" }); },

    handleFileChange(e: Event) {
      const input = e.target as HTMLInputElement;
      const f = input.files?.[0] ?? null;

      // cleanup previous URL
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = undefined;
      }

      this.file = f;
      if (f) {
        this.objectUrl = URL.createObjectURL(f);
        this.previewUrl = this.objectUrl;        // <— string for <img :src>
      } else {
        // fallback or from record if you have it
        this.previewUrl = new URL('@/assets/images/user/sin_imagen.png', import.meta.url).href;
      }
    },
    async printReport() {
      const res = await StudentService.GetReportPreEnrollment({ Id: this.studentId });
      store.dispatch("core/popupDialog", {
        text: "Archivo generado con exito.",
        type: "success",
        showCancelButton: false,
      });
      window.open(res.pathHost, '_blank')
    },
    async handleSubmit(e: Event) {
      const formComp = this.$refs.formRecord as any;
      const formEl: HTMLFormElement = formComp?.$el ?? formComp;
      this.wasValidated = true; // show invalid styling

      // 1) Native HTML5 validation for all tabs
      if (!formEl.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();
        this.jumpToFirstInvalid(formEl);
        store.dispatch("core/popupDialog", {
          text: "Faltan campos obligatorios. Revise las pestañas marcadas.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      // 2) Optional: custom cross-tab rules exposed by children
      const childRefs = [
        this.$refs.personRef,
        this.$refs.contactRef,
        this.$refs.parentsRef,
        this.$refs.academicRef,
        this.$refs.healthRef,
        this.$refs.emergencyRef,
        this.$refs.transportRef,
      ].filter(Boolean) as any[];

      for (const r of childRefs) {
        if (typeof r?.validate === "function") {
          const res = await r.validate(this.form.record);
          if (!res.ok) {
            this.jumpToPaneId(res.paneId);  // make your child return its paneId
            store.dispatch("core/popupDialog", {
              text: res.message || "Hay errores de validación.",
              type: "warning",
              showCancelButton: false,
            });
            return;
          }
        }
      }

      // 3) If all good, submit
      try {
        const formData = new FormData();
        formData.append("student", new Blob([JSON.stringify(this.form.record)], { type: "application/json" }));
        if (this.file) formData.append("picture", this.file);

        const res = await StudentService.PostStudents(formData);
        store.dispatch("core/popupDialog", {
          text: "Registro completado con éxito.",
          type: "success",
          showCancelButton: false,
        });
        this.cancel();
      } catch (err: any) {
        store.dispatch("core/popupDialog", {
          text: `Error: ${err?.response.data.message || "No se pudo guardar"}`,
          type: "error",
          showCancelButton: false,
        });
      }
    },

    jumpToFirstInvalid(formEl: HTMLFormElement) {
      // Picks the first invalid control anywhere in the form (hidden or not)
      const firstInvalid = formEl.querySelector<HTMLElement>(":invalid");
      if (!firstInvalid) return;

      // Find its tab pane & show it
      const pane = firstInvalid.closest<HTMLElement>(".tab-pane");
      if (pane?.id) this.jumpToPaneId(pane.id);

      // Focus + show native tooltip + add a quick visual highlight
      requestAnimationFrame(() => {
        // put caret/focus where the error is
        firstInvalid.focus({ preventScroll: true });
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });

        // Ask the browser to display the built-in validation bubble (Chrome/Edge/Firefox)
        // This makes the message like “Please fill out this field” appear right on the control
        if ("reportValidity" in firstInvalid) {
          (firstInvalid as any).reportValidity?.();
        }

        // Add a temporary highlight (nice glow) so the user sees it instantly
        this.flashInvalid(firstInvalid);
      });
    },

    jumpToPaneId(paneId: string) {
      const trigger = document.querySelector<HTMLElement>(`[data-bs-target="#${paneId}"]`);
      if (!trigger) return;
      const tab = new Tab(trigger);
      tab.show();
    },

    flashInvalid(el: HTMLElement) {
      // Add class to the control or its .form-control wrapper
      const target =
        el.classList.contains("form-control") || el.classList.contains("form-select")
          ? el
          : (el.querySelector(".form-control, .form-select") as HTMLElement) || el;

      target.classList.add("field-flash");
      setTimeout(() => target.classList.remove("field-flash"), 1200);
    }
  }
});
